import { Injectable, inject } from '@angular/core';
import { SsApiService } from '../api/ss-api.service';
import {
  catchError, concatMap,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { BonusStage, BonusStageLabel, BonusType } from './data/user-bonuses.data';
import { CommonDataService } from '../common-data.service';
import { GamesService } from '../games/games.service';
import { FiltersService } from '../filters.service';
import { LootboxService } from '../lootbox/lootbox.service';
import { DecimalPipe } from '@angular/common';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { UserGroupsService } from './user-groups.service';
import { UserPaymentModeService } from './user-payment-mode.service';
import { TimeService } from '../time.service';
import { UserService } from './user.service';
import { OffersService } from '../offers.service';
import { isNullOrUndefined, removeDuplicatesInObjects } from '../../helpers/utils';
import { TranslationService } from '../translation/translation.service';
import { NotificationCenterService } from '../../shared/modules/notification-center/notification-center.service';

export enum BonusCodeStatus {
  FAILED_TO_ACTIVATE = 'failed_to_activate',
  ALREADY_ACTIVATED = 'already_activated',
  SUCCESSFULLY_ACTIVATED = 'successfully_activated'
}

/**
 * Bonus pack types
 */
export const BONUS_PACKS = {
  STARTER: 'starter',
};

export const STARTER_BONUS_RANGE = [
  { min: 10, max: 133, currency: 'USD' },
  { min: 15, max: 199, currency: 'AUD' },
  { min: 100, max: 1500, currency: 'NOK' },
  { min: 10, max: 133, currency: 'EUR' },
  { min: 15, max: 199, currency: 'CAD' },
  { min: 60, max: 800, currency: 'BRL' },
];

/**
 * Use for custom bonus titles (temporary fix, in the future need request to CMS)
 */
const customBonusTitles: { title: string, translate: string }[] = [
  {
    title: 'Jackpot Bronze',
    translate: 't.jackpot-bronze',
  },
  {
    title: 'Jackpot Silver',
    translate: 't.jackpot-silver',
  },
  {
    title: 'Jackpot Gold',
    translate: 't.jackpot-gold',
  },
];

@Injectable({
  providedIn: 'root',
})
export class UserBonusesService {
  private _api = inject(SsApiService);
  private _data = inject(CommonDataService);
  private _games = inject(GamesService);
  private _filters = inject(FiltersService);
  private _lootbox = inject(LootboxService);
  private _time = inject(TimeService);
  private _groups = inject(UserGroupsService);
  private _user = inject(UserService);
  private _paymentMode = inject(UserPaymentModeService);
  private _offers = inject(OffersService);
  private _number = inject(DecimalPipe);
  private _translation = inject(TranslationService);
  private _notify = inject(NotificationCenterService);

  public amountBonusChange$: ReplaySubject<any> = new ReplaySubject<any>(1);

  public welcomeBonuses$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * All spins bonuses
   */
  public bonuses$: Observable<any>;

  /**
   * Is all welcome bonus used
   */
  public isAllBonusUsed = false;

  /**
   * Deposit bonus list
   * @private
   */
  private _depositBonusList$ = this._user.auth$.pipe(
    distinctUntilChanged(),
    switchMap(auth => {
      return auth ? this.depositBonusList() : of([]);
    }),
  );

  /**
   * CMS bonus list. Get bonuses from CMS and separate by bonus pack types
   * @private
   */
  private _cmsBonusList$: Observable<any> = this._user.currency$.pipe(
    switchMap((currency: string) => this._offers.list({
      category_slug: this._resolveCmsItemsByMode(currency),
    })),
    filter(bonuses => bonuses && bonuses.length),
    map((list: any[]) => {
      this._isSpecialStagWelcome = list.length === 3;
      this._isSpecialStagWelcomeFour = list.length === 4;
      return list;
    }),
  );

  private _welcomeBonusList$: Observable<any> = this.welcomeBonuses$;

  private _isBonusListEmpty: boolean = null;

  private _couponSuccessMessages = {
    [BonusCodeStatus.SUCCESSFULLY_ACTIVATED]: 't.bonus-activated-msg',
  };
  private _couponErrorMessages = {
    [BonusCodeStatus.ALREADY_ACTIVATED]: 't.bonus-already-activated',
    [BonusCodeStatus.FAILED_TO_ACTIVATE]: 't.failed-active-bonus',
  };

  private _isSpecialStagWelcome: boolean;
  private _isSpecialStagWelcomeFour: boolean;

  private _filteredWelcomeList$: Observable<any>;

  get isCurrentListEmpty(): boolean {
    return this._isBonusListEmpty;
  }

  get Stage() {
    return BonusStage;
  }

  get Type() {
    return BonusType;
  }

  get specialStagWelcome(): boolean {
    return this._isSpecialStagWelcome;
  }

  get specialStagWelcomeFour(): boolean {
    return this._isSpecialStagWelcomeFour;
  }

  get filteredWelcomeList$() {
    return this._filteredWelcomeList$;
  }

  public checkDifferenceMoreTwoDays() {
    if (!this._user.info.created_at) {
      return true;
    } else {
      const today = new Date();
      // ios problem handling
      const dateString = this._user.info.created_at.replace(' ', 'T').replace('UTC', '').trim() + 'Z';
      const dateRegister = new Date(dateString);
      const millisecondsDiff = today.getTime() - dateRegister.getTime();
      return Math.round(
        millisecondsDiff / (24 * 60 * 60 * 1000),
      ) >= 2;
    }
  }

  private _resolveCmsItemsByMode(currency): string {
    const cryptoItems = 'welcome-bonuses';
    const fiatItems = 'welcome-bonuses-fiat';
    if (this._user.auth && !isNullOrUndefined(currency)) {
      if (this._data.isCryptoCurrency(currency)) {
        return cryptoItems;
      }
      if (!this._data.isCryptoCurrency(currency)) {
        return fiatItems;
      }
    } else {
      if (this._paymentMode.isModeCrypto) {
        return cryptoItems;
      } else {
        return fiatItems;
      }
    }
  }

  updateBonusList() {
    this.bonuses$ = combineLatest([
      this.freeSpinsList(),
      this.bonusList(),
      this.lootBoxList(),
    ]).pipe(
      map(([fs, bonuses, lootbox]) => [...fs, ...bonuses, ...lootbox]),
      map((list: any[]) => {
        this._isBonusListEmpty = !list.length;
        return {
          [this.Stage.UNUSED]: list.filter((e) => this._isBonusForUnusedMessage(e)),
          [this.Stage.ISSUED]: list.filter(
            (e) => e.stage === this.Stage.ISSUED || e.stage === this.Stage.HANDLE_BETS,
          ),
          [this.Stage.CANCELED]: list.filter(
            (e) => e.stage === this.Stage.CANCELED,
          ),
          [this.Stage.ACTIVATED]: list.filter(
            (e) => e.stage === this.Stage.ACTIVATED && e.type !== this.Type.LOOTBOX,
          ),
          [this.Stage.EXPIRED]: list.slice(0, 3).filter(
            (e) => (e.stage === this.Stage.EXPIRED || e.stage === this.Stage.FINISHED) && e.type !== this.Type.LOOTBOX,
          ),
          [this.Stage.VIP]: list.filter((e) => (e?.isVip)),
        };
      }),
      map((list) => {
        const mergedBonuses = [
          ...list[BonusStage.ISSUED],
          ...list[BonusStage.ACTIVATED],
          ...list[BonusStage.EXPIRED],
        ];

        list[this.Stage.BONUSES_FOR_DISPLAY] = mergedBonuses;

        let lastReceived = [];
        if (mergedBonuses.length) {
          lastReceived = [...mergedBonuses].sort((a, b) => {
            return new Date(b['created_at']).getTime() - new Date(a['created_at']).getTime();
          }).splice(0, 2);
        }


        list[this.Stage.LAST_RECEIVED_DEPOSIT] = lastReceived;

        return list;
      }),
      // shareReplay(1),
    );
  }

  /**
   * Returns list of player money bonuses
   */
  bonusList(): Observable<any> {
    return this._api.playerBonuses().pipe(
      catchError(error => of([])),
      map(list => this._mapPlayerBonuses(this._mapBonuses(list))),
    );
  }

  /**
   * Returns list of player free spins bonuses
   */
  freeSpinsList(): Observable<any> {
    let games;
    let bonuses;
    return this._api.playerFreeSpins().pipe(
      catchError(error => of([])),
      map(list => this._mapFreeSpins(this._mapBonuses(list))),
      tap(list => {
        games = new Set();
        list.forEach(bonus => bonus.games.forEach(game => games.add(game)));
        bonuses = list;
      }),
      concatMap(() => games?.size !== 0 ? this._games.list({
        'external_id[]': [...games],
      }) : of([])),
      filter(response => !!response),
      map(gameResponse => {
        const gamesByExternalId = this._filters.valueAsKeyObject('externalId', gameResponse.gameList || []);
        return bonuses.map(bonus => ({
          ...bonus,
          valid_until: bonus?.valid_until || bonus?.activatable_until,
          games: (bonus.games || [])
            .map(gameId => gamesByExternalId[gameId] || {})
            .filter(e => Object.keys(e).length),
        }));
      }),
    );
  }

  /**
   * Returns list of player lootbox bonuses
   * @returns
   */
  lootBoxList(): Observable<any> {
    return this._lootbox.lootboxesTranslates$.pipe(
      first(),
      switchMap(() => {
        return this._api.playerLootbox().pipe(
          catchError(error => of([])),
          map(list => this._mapPlayerBonuses(this._mapBonuses(list))),
          map(list => list.map(e => {
            e.lootboxTitle = e?.title;
            e = { e, ...this._lootbox.resolveLootboxTranslates(e) };
            e.type = this.Type.LOOTBOX;
            e.cancelable = e.stage !== this.Stage.EXPIRED;
            e.activatable = true;
            return e;
          })),
        );
      }),
    );
  }

  /**
   * Returns list of free spins games (only activated fs)
   */
  freeSpinsActivatedGameList(): Observable<any> {
    return this.freeSpinsList().pipe(
      map(fs => fs.filter(f => f.stage === this.Stage.ISSUED || f.stage === this.Stage.ACTIVATED).map(spin => {
        if (spin.games) {
          spin.games.map((e) => e.freespins = true);
        }
        return spin?.games;
      })),
      map(gameList => {
        return { gameList: removeDuplicatesInObjects(gameList.flat(1), 'id') };
      }),
      shareReplay(),
    );
  }

  /**
   * Activate bonus by id
   *
   * @param id
   */
  activateBonus(id: string): Observable<any> {
    return this._api.playerBonusesActivation(id);
  }

  /**
   * Cancel bonus by id
   *
   * @param id
   */
  cancelBonus(id: string): Observable<any> {
    return this._api.playerBonusesCanceling(id);
  }

  /**
   * Activate free spins bonus by id
   *
   * @param id
   */
  activateFreeSpins(id: string): Observable<any> {
    return this._api.playerFreeSpinsActivation(id);
  }

  /**
   * Cancel bonus by id
   *
   * @param id
   */
  cancelFreeSpins(id: string): Observable<any> {
    return this._api.playerFreeSpinsCanceling(id);
  }

  /**
   * Checks if title contains WCM which indicates that it's a welcome bonus
   *
   * @param list
   * @private
   */
  private _isWelcomeBonus(title: string) {
    return ['WLC', 'WCM', 'AFF'].some(key => title.toUpperCase().includes(key));
  }

  /**
   * Prepare bonus list for using in frontend
   *
   * @param list
   * @private
   */
  private _mapBonuses(list: Array<any>): Array<any> {
    return list.map(bonus => {
      return {
        ...bonus,
        isWelcome: this._isWelcomeBonus(bonus.title),
        isVip: this._isVipBonus(bonus.title),
        title: this.mapBonusTitle(bonus.title),
        active: ![BonusStage.LOST, BonusStage.CANCELED, BonusStage.PLAYED].includes(bonus.stage),
        stage_label: BonusStageLabel[bonus.stage] || bonus.stage,
        created_at: bonus.created_at ? new Date(bonus.created_at) : null,
        activatable_until: bonus.activatable_until ? new Date(bonus.activatable_until) : null,
        valid_until: bonus.valid_until ? new Date(bonus.valid_until) : null,
        type: 'money',
      };
    });
  }

  private _isVipBonus(title: string) {
    return ['VIP'].some(key => title.toUpperCase().includes(key));
  }

  /**
   * Prepare bonus title for using in frontend
   *
   * @private
   */
  public mapBonusTitle(title: string): string {
    return (title || '')
      .replace(/\[(.*?)]/g, '')
      .replace(/\[(.*?)}/g, '')
      .replace(/\{(.*?)]/g, '')
      .replace(/\{(.*?)}/g, '');
  }

  /**
   * Prepare player bonuses for using if frontend
   *
   * @param list
   * @private
   */
  private _mapPlayerBonuses(list: Array<any>): Array<any> {
    return list.map(bonus => ({
      ...bonus,
      currency_symbol: this._data.currencySymbol(bonus.currency),
      amount: this._data.subunitsToUnits(bonus.amount_cents, bonus.currency),
      amount_wager_requirements: this._data.subunitsToUnits(bonus.amount_wager_requirement_cents, bonus.currency),
      amount_wager: this._data.subunitsToUnits(bonus.amount_wager, bonus.currency),
      wager: (bonus.amount_wager_requirement_cents ?
        (bonus.amount_wager_cents / bonus.amount_wager_requirement_cents * 100) :
        100),
      title: this._customTitleForBonus(bonus?.title),
    }));
  }

  /**
   * Prepare free spins bonuses for using in frontend
   *
   * @param list
   * @private
   */
  private _mapFreeSpins(list: Array<any>): Array<any> {
    return list.map(bonus => ({
      ...bonus,
      freespins_performed: bonus.freespins_performed || 0,
      type: 'free_spins',
    }));
  }

  /**
   * Bonus deposit code activation
   *
   * @param code
   */
  public activateDepositBonusCode(code): Observable<any> {
    return this._api.playerSetBonusCode({ deposit_bonus_code: (code || '').toUpperCase() });
  }

  /**
   * Bonus deposit code activation
   *
   * @param code
   */
  public activateCouponBonusCode(code): Observable<any> {
    return this._api.playerSetCoupon({ coupon_code: (code || '').toUpperCase() });
  }

  /**
   * Bonus deposit code clear
   *
   */
  public deleteDepositBonusCode(): Observable<any> {
    return this._api.playerClearBonusCode();
  }

  /**
   * Returns list of bonuses that will be activated on next deposit
   */
  depositBonusList(): Observable<any> {
    return this._api.bonusesDeposit().pipe(
      map(list => {
        return (list || []).map(bonus => {
          const preparedBonus = {};

          bonus.bonuses.forEach(bonusPart => {
            preparedBonus[bonusPart.type] = {
              id: bonus.id,
              title: this.mapBonusTitle(bonusPart.title),
              attributes: this.resolveBonusAttributes(bonusPart.attributes),
              conditions: this.resolveBonusAttributes(bonusPart.conditions),
            };

            /**
             * For show another part of freespins title on deposit page
             */
            if (bonusPart?.title?.includes('WCM') && bonusPart?.title?.includes('FS') && bonusPart?.type === 'bonus') {
              preparedBonus['freespins_deposit'] = {
                count: Number(bonusPart?.title?.match(/\+(\s*\d+)/)[1].trim()),
              };
            }
          });

          return preparedBonus;
        });
      }),
      catchError(error => of([])),
    );
  }

  /**
   * Returns object from array of attributes
   *
   * @private
   * @param list
   */
  public resolveBonusAttributes(list: Array<any>) {
    const attributes: any = {};

    list.forEach(attribute => {
      if (attribute.field === 'bonus_amount' && attribute.type === 'max') {
        attributes.bonus_amount_max = this._resolveAmountList(attribute.value);
      } else if (attribute.field === 'bonus_amount' && attribute.value.percent) {
        attributes.bonus_amount_percent = attribute.value.percent;
      } else if (attribute.field === 'amount' && attribute.type === 'min') {
        attributes.amount_min = this._resolveAmountList(attribute.value);
      } else if (attribute.field === 'amount' && attribute.type === 'max') {
        attributes.amount_max = this._resolveAmountList(attribute.value);
      } else {
        attributes[attribute.field] = attribute.value;
      }
    });

    return attributes;
  }

  /**
   * Convert amounts list from backend to object where keys is currency and values is converted amount
   *
   * @param list
   * @private
   */
  private _resolveAmountList(list: Array<any>) {
    const amounts = {};

    list.forEach(amount => {
      amounts[amount.currency] = this._data.subunitsToUnits(amount.amount_cents, amount.currency);
    });

    return amounts;
  }

  /**
   * Send request on update bonus settings
   */
  changeIssues(state: boolean) {
    return this._api.playerUpdateBonusSettings({ can_issue: state });
  }

  /**
   * Clear bonus title ([WCM*] First Deposit Bonus -> First Deposit Bonus )
   * @param bonus
   */
  private _clearBonusTitle(bonus: { title: string }) {
    if (bonus.title) {
      bonus.title = bonus.title.replace(/\[([^)]+)\]/gi, '').trim();
    }
  }

  /**
   * Compare bonus list and return active bonus
   * @param bonusListCMS
   * @param bonusListSS
   * @param bonusPack
   * @returns
   */
  public findActiveBonus(bonusListCMS: any[], bonusListSS: any[], bonusPack?: string) {
    return bonusListCMS.find(cmsBonus => {
      return bonusListSS.find(ssBonus => {
        if (ssBonus?.bonus?.id?.includes(cmsBonus.BOIdentifierStarter) ||
          ssBonus?.freespins?.id?.includes(cmsBonus.BOIdentifierStarter)) {
          cmsBonus.bonus_amount_max = ssBonus?.bonus?.attributes?.bonus_amount_max;
          cmsBonus.bonus_amount_percent = ssBonus?.bonus?.attributes?.bonus_amount_percent;
          return ssBonus;
        } else {
          return null;
        }
      });
    });
  }

  /**
   * Resolve coupon error message according the status
   * @param response
   */
  public resolveCouponResponseMessage(response) {
    const status = response.status;
    if (status === BonusCodeStatus.SUCCESSFULLY_ACTIVATED) {
      return this._getResponse(this._couponSuccessMessages[status], 'code_success');
    } else if (status === BonusCodeStatus.FAILED_TO_ACTIVATE) {
      return this._getResponse(this._couponErrorMessages[status], 'code_error');
    } else {
      return this._getResponse('t.undefined-error', 'code_error');
    }
  }


  /**
   * Add SS structure response to correct resolve errors for input
   * @param message
   * @param key
   * @private
   */
  private _getResponse(message: string, key: string): any {
    return {
      errors: {
        code: {
          [key]: message,
        },
      },
    };
  }

  /**
   * Is bonus for unused notification message
   * @param bonus
   * @private
   */
  private _isBonusForUnusedMessage(bonus: any): boolean {
    if ((bonus.stage !== this.Stage.ISSUED && bonus.stage !== this.Stage.HANDLE_BETS) || !bonus?.valid_until) return false;

    const timeUntilEnds = this._time.timeDiff(bonus.activatable_until || bonus.valid_until);
    return (bonus.stage === this.Stage.ISSUED || bonus.stage === this.Stage.HANDLE_BETS) && (timeUntilEnds.hours < 24 && timeUntilEnds.days === 0);
  }

  /**
   * Mark active bonus and resolve active bonus image
   * @param bonusListCMS
   * @param bonusListSS
   * @param bonusPack
   */
  public markActiveBonus(bonusListCMS: any[], bonusListSS: any[]) {
    if (this._user.auth) {
      if (this._groups.isAllWelcomeUsed()) {
        this.isAllBonusUsed = true;
        bonusListCMS.forEach(e => {
          e.used = true;
        });
      } else {
        bonusListCMS.forEach(e => e.active = false);
        let activeBonus = this.findActiveBonus(bonusListCMS, bonusListSS);
        if (!activeBonus) {
          activeBonus = bonusListCMS[0];
        }
        const activeBonusIndex = bonusListCMS.findIndex(e => e.id === activeBonus.id);
        bonusListCMS.forEach((e, i) => {
          if (i < activeBonusIndex) {
            e.used = true;
          }
        });
        activeBonus.active = true;
      }
      return bonusListCMS;
    } else {
      bonusListCMS[0].active = true;
      return bonusListCMS;
    }
  }

  /**
   * Get merged bonuses from CMS and SS
   * @private
   */
  public getMergedBonuses$(): Observable<any> {
    return combineLatest([this._cmsBonusList$, this._depositBonusList$]).pipe(
      map(([bonusListCms, bonusListSS]) => this.markActiveBonus(bonusListCms, bonusListSS)),
      map(list => this._markActiveBonusIdx(list)),
      tap(data => {
        this.welcomeBonuses$.next(data);
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  public resolveBonusByAmount(amount: number, isCryptoCalculate = false, forPreviousActiveBonus = false) {
    const starter = Boolean(STARTER_BONUS_RANGE.find(item =>
      item.currency === this._user.currentCurrency.currency &&
      amount >= item.min && amount <= item.max));

    this._filteredWelcomeList$ = this._welcomeBonusList$.pipe(
      map(list => list || []),
      map(list =>
        list.map(item => {
          const currencySymbolsStringPattern = `[${this._user.accountList.map(s => s?.symbol).join('')}]\\d+(?:\\.\\d+)? *`;
          const description = item.DescriptionStarterBonus || item.Description;
          const descriptionMatch = description?.match(new RegExp(currencySymbolsStringPattern, 'g'));

          let upToBonusAmount;

          if (!isNullOrUndefined(descriptionMatch)) {
            upToBonusAmount = description?.match(new RegExp(currencySymbolsStringPattern, 'g'))[0];
          }

          return {
            ...item,
            percentage: item?.bonus_amount_percent,
            upToAmount: upToBonusAmount ? Number(upToBonusAmount.slice(1)) : null,
          };
        }),
      ),
      map(list => {
        if (!forPreviousActiveBonus) {
          return list.find(item => item.active);
        } else {
          const activeIndex = list.findIndex(item => item.active);
          return activeIndex < 1 ? list[activeIndex] : list[activeIndex - 1];
        }
      }),
    );

    this._filteredWelcomeList$.subscribe((item) => {

      let calculatedBonus = this._number.transform(amount + (amount * item?.percentage) / 100);
      const upToAmountBonus = amount ? this._number.transform((amount * item?.percentage) / 100) : null;
      const bonusFs = (item?.SecondDescriptionStarterBonus || item?.SecondDescription)?.replace(/<[^>]+>/g, '');

      if (item?.bonus_amount_max) {
        if (((amount * item.percentage) / 100) > item?.bonus_amount_max[this._user.currentCurrency.currency]) {
          calculatedBonus = this._number.transform(amount + item?.bonus_amount_max[this._user.currentCurrency.currency]);
        }
      }

      if (calculatedBonus) {
        this.amountBonusChange$.next({
          bonusAmount: `${isCryptoCalculate ? '~' : ''} ${calculatedBonus} <span>${this._user.currentCurrency.currency}</span>`,
          bonusFs: bonusFs ? `+ ${bonusFs}` : null,
          upToBonusAmount: upToAmountBonus && upToAmountBonus < item?.upToAmount ? `${isCryptoCalculate ? '~' : ''} ${upToAmountBonus} <span>${this._user.currentCurrency.currency}</span>` : null,
        });
      } else {
        this.amountBonusChange$.next(null);
      }
    });
  }

  /**
   * Mark active bonus idx
   * @param list
   * @private
   */
  private _markActiveBonusIdx(list: any[]): any[] {
    return list.map(item => {
      return {
        ...item,
        activeBonusIdx: list.findIndex(e => e.active),
      };
    });
  }

  /**
   *
   * @param title
   * @private
   */
  private _customTitleForBonus(title: string) {
    const translate = customBonusTitles.find(t => t.title === title);
    return translate?.translate ? this._translation.translate(translate?.translate) : title;
  }
}
